import React, { useCallback, useState, useEffect, useRef } from 'react';
import useIsMobile from '../../../hooks/useIsMobile';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { Button, Divider, Typography } from 'antd';
import useAudioCache from '../../../hooks/useAudioCache';
import { randomInt, shuffled } from '../../../utils/random';

const { Text } = Typography;


/**
 * Подсветка нажатого варианта: зелёная — верный, красная — нет. Остальные кнопки
 * остаются белыми с серой рамкой, пока по ним не нажали.
 */
const answerBackground = (buttonState, index) => {
    if (buttonState.index !== index) {
        return 'white';
    }
    return buttonState.isCorrect ? '#d4edda' : '#f8d7da';
};

const answerBorderColor = (buttonState, index) => {
    if (buttonState.index !== index) {
        return '#ccc';
    }
    return buttonState.isCorrect ? '#28a745' : '#dc3545';
};

function WordTranslationChoiceCard({ word, locale, handleCorrectClick, handleIncorrectClick }) {
    const isMobile = useIsMobile();
    // Кэш на карточку: варианты — разные слова, но их ref уникальны сами по себе.
    const { playAudio } = useAudioCache(
        `${word.headword}|${word.partOfSpeech}|${locale.code}`, locale.code);
    const [translationOptions, setTranslationOptions] = useState([]);
    const [buttonState, setButtonState] = useState({ index: null, isCorrect: null });
    // Сколько раз нажали и когда показали слово: и то и другое нужно журналу, а
    // восстановить их потом неоткуда.
    const attemptsRef = useRef(0);
    const shownAtRef = useRef(Date.now());
    // Флаг, который запоминает, что была допущена ошибка
    const [hasError, setHasError] = useState(false);

    const generateOptions = useCallback(() => {
        // Выбираем случайный вариант из главного списка определений
        const mainDefs = word.wordDefinition || [];
        const correctOption = mainDefs.length > 0 ? (() => {
            const randomIndex = randomInt(mainDefs.length);
            const def = mainDefs[randomIndex];
            return {
                translation: def.translation,
                definition: def.definition,
                isCorrect: true,
                headword: word.headword,        // Add main word's headword
                phonetic: word.phonetic,        // Add main word's phonetic
                partOfSpeech: word.partOfSpeech, // Add main word's partOfSpeech
                audioRef: word.audio?.access === 'ALLOWED' ? word.audio.ref : null,
            };
        })() : null;

        // Для auxiliaryItems выбираем из каждого случайный вариант определения
        const auxOptions = word.auxiliaryItems?.map(item => {
            const defs = item.wordDefinition;
            if (defs && defs.length > 0) {
                const randomIndex = randomInt(defs.length);
                const def = defs[randomIndex];
                return {
                    translation: def.translation,
                    definition: def.definition,
                    isCorrect: false,
                    headword: item.headword,          // Add auxiliary item's headword
                    phonetic: item.phonetic,          // Add auxiliary item's phonetic
                    partOfSpeech: item.partOfSpeech,   // Add auxiliary item's partOfSpeech
                    audioRef: item.audio?.access === 'ALLOWED' ? item.audio.ref : null,
                };
            }
            return null;
        }).filter(option => option !== null) || [];

        // Берем случайно до 3-х вариантов из дополнительных
        const filteredAux = shuffled(auxOptions).slice(0, 3);

        // Объединяем корректный вариант с дополнительными
        const combined = correctOption ? [correctOption, ...filteredAux] : [...filteredAux];
        const finalOptions = shuffled(combined).map((option, index) => ({
            ...option,
            id: index,
        }));

        setTranslationOptions(finalOptions);
    }, [word]);

    useEffect(() => {
        if (!word) return;
        // При загрузке нового слова сбрасываем состояние ошибки
        setHasError(false);
        generateOptions();
        attemptsRef.current = 0;
        shownAtRef.current = Date.now();
    }, [word, generateOptions]);

    // Озвучка варианта — свой ref у каждого: и у главного слова, и у отвлекающих.
    const playOptionAudio = (option) => {
        playAudio(option.audioRef);
    };

    const handleTranslationClick = async (option, index) => {
        // Сохраняем текущее состояние ошибки
        const wasError = hasError;
        const chosenAt = Date.now();
        attemptsRef.current += 1;
        setButtonState({ index, isCorrect: option.isCorrect });

        // Произносим слово при любом клике на кнопку; задержки ниже дают звуку уйти.
        playOptionAudio(option);

        if (!option.isCorrect) {
            // Если выбран неверный ответ, запоминаем ошибку и показываем анимацию ошибки,
            // но не переходим дальше
            setHasError(true);
            setTimeout(() => {
                setButtonState({ index: null, isCorrect: null });
            }, 500);
        } else {
            // Если выбран правильный вариант, переходим дальше только если выбран правильный ответ.
            // Но если ранее была допущена ошибка, финальный результат остаётся неверным.
            // Добавляем небольшую задержку, чтобы аудио точно воспроизвелось
            setTimeout(() => {
                setButtonState({ index: null, isCorrect: null });
                // Время раздумья замеряем по моменту выбора, а не по моменту вызова:
                // между ними полсекунды анимации, и они попали бы в журнал как раздумье.
                const evidence = {
                    optionCount: translationOptions.length,
                    attempts: attemptsRef.current,
                    responseMs: Math.max(0, chosenAt - shownAtRef.current),
                };
                if (wasError) {
                    handleIncorrectClick(evidence);
                } else {
                    handleCorrectClick(evidence);
                }
            }, 800); // Увеличили задержку до 800ms
        }
    };

    return (
        <div
            style={{
                padding: isMobile ? '0' : '0px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: 'calc(100vh - 80px)',
                overflow: 'hidden',
                width: isMobile ? '100%' : '33%',
                margin: isMobile ? '0' : 'auto',
            }}
        >
            <Card style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', padding: '0px' }}>
                <CardContent style={{ flexGrow: 1, padding: isMobile ? '0px' : '20px' }}>
                    <div>
                        {/* Displaying definitions and translations for the current word */}
                        <div style={{ marginTop: '20px' }}>
                            {word.wordDefinition?.map((def, index) => (
                                <div
                                    key={def.id}
                                    style={{
                                        padding: '0px 5px',
                                        margin: '10px 0',
                                    }}
                                >
                                    <div style={{ textAlign: 'center', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                        <Text strong style={{ display: 'block', textAlign: 'left' }}>
                                            {def.definition}
                                        </Text>
                                        <Text italic strong style={{ display: 'block', textAlign: 'center', marginLeft: '12px', color: '#ee6002' }}>
                                            {def.translation}
                                        </Text>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <Divider style={{ margin: isMobile ? '0px 0' : '10px 0' }} />

                    <div style={{ marginTop: '30px' }}>
                        {translationOptions.map((option, index) => (
                            <Button
                                key={option.id}
                                onClick={() => handleTranslationClick(option, index)}
                                style={{
                                    display: 'block',
                                    margin: '10px auto',
                                    width: '100%',
                                    fontSize: '1.2em',
                                    backgroundColor: answerBackground(buttonState, index),
                                    borderColor: answerBorderColor(buttonState, index),
                                    transition: 'background-color 0.3s ease',
                                    padding: '12px 16px',
                                    height: 'auto',
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word',
                                }}
                            >
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    marginBottom: '0px'
                                }}>
                                    <Text strong style={{
                                        fontSize: '22px',
                                        color: '#027b83',
                                        textAlign: 'center',
                                        marginBottom: '4px'
                                    }}>
                                        {option.headword}
                                    </Text>

                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}>
                                        <Text style={{ fontSize: '15px' }}>/{option.phonetic}/</Text>
                                        <Text strong style={{ fontSize: '18px' }}>{option.partOfSpeech}</Text>
                                    </div>
                                </div>
                            </Button>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export default WordTranslationChoiceCard;
